import type { Client } from '../client';
import { withApp } from '../with-app';
import type {
  ApprovalRequest,
  ApprovalDecisionRequest,
  ApprovalDecisionResponse,
  PaginatedResponse,
} from '../types';

export function approvalEndpoints(client: Client) {
  return {
    listPending: (hospitalId?: string, opts?: { includeRecent?: number }) => {
      const qs = new URLSearchParams({ status: 'pending' });
      if (opts?.includeRecent) qs.set('include_recent', String(opts.includeRecent));
      const query = qs.toString();
      const path = hospitalId
        ? `/api/approval-requests/hospital/${hospitalId}?${query}`
        : `/api/approval-requests?${query}`;
      return client.get<ApprovalRequest[]>(path);
    },

    getById: (id: number) =>
      client.get<ApprovalRequest>(`/api/approval-requests/${id}`),

    decide: (id: number, data: ApprovalDecisionRequest) =>
      client.post<ApprovalDecisionResponse>(
        `/api/approval-requests/${id}/decide`,
        data,
      ),

    /** Re-route a still-pending case. null = back to the coordinator's queue. */
    moveDepartment: (id: number, departmentId: number | null) =>
      client.put<{ status: string; department_id: number | null }>(
        `/api/approval-requests/${id}/department`,
        { department_id: departmentId },
      ),

    history: (params?: {
      hospitalId?: string;
      page?: number;
      pageSize?: number;
      decision?: 'accept' | 'reject' | 'missed';
      search?: string;
    }) => {
      const p = params ?? {};
      const qs = new URLSearchParams();
      if (p.page) qs.set('page', String(p.page));
      if (p.pageSize) qs.set('page_size', String(p.pageSize));
      if (p.decision) qs.set('decision', p.decision);
      if (p.search) qs.set('search', p.search);
      const base = p.hospitalId
        ? `/api/approval-requests/history/${p.hospitalId}`
        : '/api/approval-requests/history';
      const query = qs.toString();
      return client.get<PaginatedResponse<ApprovalRequest>>(
        query ? `${base}?${query}` : base,
      );
    },

    confirmed: (params?: { hospitalId?: string; page?: number; pageSize?: number }) => {
      const p = params ?? {};
      const qs = new URLSearchParams();
      if (p.page) qs.set('page', String(p.page));
      if (p.pageSize) qs.set('page_size', String(p.pageSize));
      const base = p.hospitalId
        ? `/api/approval-requests/confirmed/${p.hospitalId}`
        : '/api/approval-requests/confirmed';
      const query = qs.toString();
      return client.get<PaginatedResponse<ApprovalRequest>>(
        query ? `${base}?${query}` : base,
      );
    },

    getDetail: (id: number) =>
      client.get<ApprovalRequest>(`/api/approval-requests/detail/${id}`),

    // Opened via <a href>, so it carries no X-Client-App — see withApp().
    getAttachmentUrl: (fileId: number, filename?: string) =>
      withApp(client, filename
        ? `/api/attachments/${fileId}/${encodeURIComponent(filename)}`
        : `/api/attachments/${fileId}`),
  };
}
